import { useAxios, useSubjectId } from '@healthblocks-io/core'
import { useCallback } from 'react'

import { useAxiosAsync } from '@/@bothrs-react/hooks/useAxiosAsync'
import { mixpanel } from '@/mixpanel'
import { sortByDate } from '@/modules/shared/helpers/sort-by-date'
import { useNotificationCenterState } from '../state/useNotificationState'

import type { CareplanActivity } from '@healthblocks-io/core'
import type {
  PatientCommunication,
  PatientCommunicationMessage,
} from '@/modules/care-question/types/patient-communication.type'
import type { NotificationMessage } from '../types/notificiation-types'

const useNotificationCenter = () => {
  const axios = useAxios()
  const subjectId = useSubjectId()

  const { setNotificationCenterState, notificationCenterState } = useNotificationCenterState()

  const getNotifications = useCallback(async () => {
    return axios
      .get<CareplanActivity[]>('careplan-activity', {
        params: {
          subjectId: subjectId,
          include: ['careplan'].join(','),
          type: ['Message', 'PatientCommunication'].join(','),
          actorRole: ['subject'].join(','),
        },
      })
      .then((response) => {
        const notifications = response.data?.filter((notification) => notification.status !== 'proposed')

        const mappedNotifications = notifications
          .map((notification) => {
            if (notification.type === 'Message') {
              const mappedNotification: NotificationMessage = {
                id: notification.id,
                display: notification.display || notification.meta?.message || '',
                description: notification.meta?.title || notification.meta?.message,
                read: notification.status === 'completed' || notification.status === 'expired',
                type: notification.meta.messageType || 'Message',
                date: new Date(notification.plannedAt),
                attachment: (() => {
                  const att = notification?.meta?.attachments?.at(0)
                  return att && 'url' in att ? att.url : undefined
                })(),
                meta: notification.meta as Record<string, unknown>,
              }
              return mappedNotification
            } else {
              const patientCommunication = notification as PatientCommunication

              const latestReceivedSender = patientCommunication?.meta?.messages
                ?.filter((message) => message.sender.type !== 'patient')
                ?.sort(sortByDate<PatientCommunicationMessage>(['createdAt'], 'DESC'))
                ?.at(0)?.sender

              const latestMessage = patientCommunication.meta?.messages
                ?.sort(sortByDate<PatientCommunicationMessage>(['createdAt'], 'DESC'))
                ?.at(0)

              const readStatus = latestMessage?.readStatus?.patient
                ? new Date(latestMessage?.readStatus?.patient)
                : undefined
              const isRead = !!readStatus

              const mappedNotification: NotificationMessage = {
                id: patientCommunication.id,
                display: patientCommunication.display || '',
                description: latestReceivedSender?.name || '',
                read: isRead,
                type: 'PatientCommunication',
                date: new Date(latestMessage?.createdAt || patientCommunication.plannedAt),
                meta: patientCommunication.meta,
              }
              return mappedNotification
            }
          })
          ?.sort(sortByDate<NotificationMessage>(['date'], 'DESC'))

        const unreadMessages = mappedNotifications?.filter((notification) => !notification.read)

        setNotificationCenterState({
          notifications: mappedNotifications,
          unreadNotifications: unreadMessages.length,
          isLoading: false,
        })

        return {
          ...response,
          data: mappedNotifications,
        }
      })
  }, [subjectId, axios, setNotificationCenterState])

  const setNotificationAsRead = useCallback(
    (id: number) => {
      setNotificationCenterState((current) => ({
        ...current,
        notifications: current.notifications.map((notification) => {
          if (notification.id === id) {
            return { ...notification, read: true, status: 'completed' }
          }
          return notification
        }),
        unreadNotifications: notificationCenterState.unreadNotifications - 1,
      }))

      mixpanel.track('message_read', { id, origin })

      return axios.patch(`/careplan-activity/${id}`, {
        status: 'completed',
      })
    },
    [axios, notificationCenterState.unreadNotifications, setNotificationCenterState],
  )

  const setPatientCommunicationAsRead = useCallback(
    (id: number) => {
      setNotificationCenterState((current) => ({
        ...current,
        notifications: current.notifications.map((notification) => {
          if (notification.id === id) {
            return { ...notification, read: true }
          }
          return notification
        }),
        unreadNotifications: notificationCenterState.unreadNotifications - 1,
      }))

      mixpanel.track('message_read', { id, origin })
      return axios.patch(`/communication/${id}/read`, {
        role: 'patient',
      })
    },
    [axios, notificationCenterState.unreadNotifications, setNotificationCenterState],
  )

  return {
    notifications: notificationCenterState.notifications,
    notificationsLoading: notificationCenterState.isLoading,
    hasUnreadNotifications: notificationCenterState.unreadNotifications > 0,
    numberOfUnreadNotifications: notificationCenterState.unreadNotifications,
    getNotifications: useAxiosAsync(getNotifications),
    setNotificationAsRead: useAxiosAsync(setNotificationAsRead),
    setPatientCommunicationAsRead: useAxiosAsync(setPatientCommunicationAsRead),

    notificationFetcher: getNotifications,
  }
}

export { useNotificationCenter }
